import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import {
  AddRounded as AddIcon,
  AutoAwesomeRounded as AutoIcon,
  CategoryRounded as ItemIcon,
  RestoreRounded as RestoreIcon,
} from "@mui/icons-material";
import {
  BLUE,
  BLUE_DARK,
  BLUE_LIGHT,
  BLUE_PALE,
  BORDER,
  INK,
  INK_MUTED,
  INK_SOFT,
  WARN,
  fieldSx,
  getFieldSx,
} from "./shared";
import { apiErrorMessage } from "../../../utils/apiError";
import {
  CODE_MAX,
  NAME_MAX,
  createItemType,
  normalizeItemName,
  normalizeShortCode,
  rankItemTypes,
  suggestShortCode,
  takenCodesIn,
} from "../itemTypes";

// Where focus goes once an item is picked — the Pcs field of the ornament row.
export const ORNAMENT_PCS_INPUT_ID = "ornament-pcs-input";
const focusPcs = () =>
  setTimeout(() => document.getElementById(ORNAMENT_PCS_INPUT_ID)?.focus(), 0);

const CREATE = "__create__";

const CodeChip = ({ code, inverted }) => (
  <Chip
    label={code}
    size="small"
    sx={{
      height: 20,
      fontSize: "0.64rem",
      fontWeight: 800,
      fontFamily: "monospace",
      letterSpacing: "0.05em",
      bgcolor: inverted ? "rgba(255,255,255,0.2)" : BLUE_PALE,
      color: inverted ? "#fff" : BLUE_DARK,
      border: `1px solid ${inverted ? "transparent" : BLUE_LIGHT}`,
    }}
  />
);

// ─── Item type of the ornament being added ──────────────────────────────────
// Replaces the old free-text "Item Name". Typing searches the metal's item
// types by NAME or SHORT CODE (an exact code wins: "CH" → CHAIN), and when
// nothing is called what was typed, the last option creates it — so a teller
// never leaves the loan to go and key a new item type in the master.
//
// `allItemTypes` carries deleted rows too: typing a deleted name offers to
// RESTORE it (the server does exactly that), rather than pretending it is new.
const ItemTypePicker = forwardRef(function ItemTypePicker({
  allItemTypes,
  metalId,
  metalName,
  itemTypeId,
  itemName,
  onPick,
  onCreated,
  storeId,
  userId,
  branchId,
  showSnackbar,
  error,
  disabled,
  onCreateCancelled,
}, ref) {
  const [inputValue, setInputValue] = useState(itemName || "");
  const [dialog, setDialog] = useState(null); // { name, code, codeTouched, restoring }
  const [saving, setSaving] = useState(false);
  const [dialogErrors, setDialogErrors] = useState({});
  // The list is controlled so it can be shut while the create dialog is up —
  // left to itself it stays open (the input keeps focus) and its portal sits
  // on top of the dialog.
  const [listOpen, setListOpen] = useState(false);
  const inputRef = useRef(null);

  const options = useMemo(
    () =>
      (allItemTypes || []).filter(
        (t) => t.is_active && Number(t.metal_type_id) === Number(metalId),
      ),
    [allItemTypes, metalId],
  );

  // The picked value, built from the ornament itself rather than looked up in
  // the options: an old loan may hold an item type deleted since, and it must
  // still show.
  const value = itemTypeId ? { id: itemTypeId, name: itemName || "" } : null;

  // Follow the ornament when it changes from outside (Edit on a row, reset
  // after Add, metal switch).
  useEffect(() => {
    setInputValue(itemName || "");
  }, [itemTypeId, itemName]);

  const findDeleted = (name) => {
    const n = normalizeItemName(name);
    return (
      (allItemTypes || []).find(
        (t) =>
          !t.is_active &&
          Number(t.metal_type_id) === Number(metalId) &&
          normalizeItemName(t.name) === n,
      ) || null
    );
  };

  const filterOptions = (opts, state) => {
    const typed = state.inputValue;
    // An untouched field showing the picked name lists everything.
    const query = value && typed === value.name ? "" : typed;
    const ranked = rankItemTypes(opts, query);
    const n = normalizeItemName(query);
    if (n && !opts.some((t) => normalizeItemName(t.name) === n)) {
      const deleted = findDeleted(n);
      ranked.push({ id: CREATE, name: n, restoring: deleted });
    }
    return ranked;
  };

  // forAdd: opened by the ornament's Add button rather than the list — the
  // dialog then says the item type must be saved first, and saving finishes
  // the Add.
  const openCreate = (name, { forAdd = false } = {}) => {
    setListOpen(false);
    inputRef.current?.blur();
    const restoring = findDeleted(name);
    setDialogErrors({});
    setDialog({
      name: normalizeItemName(name),
      code: restoring ? restoring.short_code : "",
      codeTouched: Boolean(restoring),
      restoring,
      forAdd,
    });
  };

  // Back to the field with the typed name intact, so the teller can correct a
  // typo and pick an existing item instead.
  const cancelDialog = () => {
    setDialog(null);
    onCreateCancelled?.();
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  // For the Payment screen's Add:
  //   typedText()   → what is typed but not yet an item type ("" when the
  //                   field is empty or shows the picked item)
  //   settleTyped() → turn that text into an item type: the exact name/code
  //                   match is picked; anything else reopens Create — every
  //                   time, until it is saved or the text is changed.
  const unsettledText = () => {
    const typed = String(inputValue || "").trim();
    if (!typed || (value && inputValue === value.name)) return "";
    return typed;
  };
  useImperativeHandle(ref, () => ({
    typedText: unsettledText,
    settleTyped: ({ forAdd = false } = {}) => {
      const typed = unsettledText();
      if (!typed) return "empty";
      const q = typed.toUpperCase();
      const exact = options.find(
        (t) =>
          t.short_code.toUpperCase() === q ||
          normalizeItemName(t.name) === normalizeItemName(q),
      );
      if (exact) {
        onPick(exact);
        return "picked";
      }
      openCreate(typed, { forAdd });
      return "creating";
    },
  }));

  const pick = (row) => {
    onPick(row);
    if (row) focusPcs();
  };

  // Exact name or code typed and the field left without choosing: that is a
  // choice, take it.
  const pickExactOnBlur = () => {
    const q = String(inputValue || "").trim().toUpperCase();
    if (!q || (value && value.name === inputValue)) return;
    const exact = options.find(
      (t) => t.short_code.toUpperCase() === q || normalizeItemName(t.name) === normalizeItemName(q),
    );
    if (exact) onPick(exact);
  };

  // ── Create dialog ───────────────────────────────────────────────────────
  const taken = useMemo(
    () => takenCodesIn(allItemTypes, branchId),
    [allItemTypes, branchId],
  );
  const dialogCode = dialog
    ? dialog.codeTouched
      ? dialog.code
      : suggestShortCode(dialog.name, taken)
    : "";
  const dialogNameClash =
    dialog &&
    options.find((t) => normalizeItemName(t.name) === normalizeItemName(dialog.name));
  const dialogCodeClash =
    dialog &&
    !dialog.restoring &&
    (allItemTypes || []).find(
      (t) =>
        (branchId == null || Number(t.branch_id) === Number(branchId)) &&
        String(t.short_code).toUpperCase() === normalizeShortCode(dialogCode),
    );

  const saveDialog = async () => {
    if (!dialog || saving) return;
    const errs = {};
    if (!normalizeItemName(dialog.name)) errs.name = "Item type name is required";
    else if (dialogNameClash) errs.name = `${dialogNameClash.name} already exists`;
    if (!normalizeShortCode(dialogCode)) errs.code = "Short code is required";
    else if (dialogCodeClash)
      errs.code = `Used by ${dialogCodeClash.name}${dialogCodeClash.is_active ? "" : " (deleted)"}`;
    if (Object.keys(errs).length) {
      setDialogErrors(errs);
      showSnackbar(
        errs.name?.endsWith("required") || errs.code?.endsWith("required")
          ? "Please fill all mandatory fields"
          : Object.values(errs)[0],
        "warning",
      );
      return;
    }
    setSaving(true);
    try {
      const res = await createItemType(storeId, {
        metal_type_id: metalId,
        name: dialog.name,
        short_code: dialogCode,
        ...(branchId != null ? { branch_id: branchId } : {}),
        user_id: userId,
      });
      const row = res?.data;
      if (row) {
        onCreated(row);
        onPick(row);
      }
      setDialog(null);
      showSnackbar(res?.message || "Item type created", "success");
      // From Add, the Add finishes now and moves focus on itself.
      if (!dialog.forAdd) focusPcs();
    } catch (err) {
      showSnackbar(apiErrorMessage(err, "Failed to create item type"), "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Autocomplete
        value={value}
        inputValue={inputValue}
        options={options}
        open={listOpen && !dialog}
        onOpen={() => setListOpen(true)}
        onClose={() => setListOpen(false)}
        disabled={disabled || !metalId}
        autoHighlight
        openOnFocus
        clearOnBlur={false}
        selectOnFocus
        handleHomeEndKeys
        filterOptions={filterOptions}
        isOptionEqualToValue={(o, v) => o.id === v?.id}
        getOptionLabel={(o) => (typeof o === "string" ? o : o?.name || "")}
        noOptionsText={
          <Typography sx={{ fontSize: "0.8rem", color: INK_MUTED }}>
            Type a name or short code — new ones can be created here
          </Typography>
        }
        onInputChange={(_e, next, reason) => {
          if (reason === "reset") return; // the effect above owns resets
          const upper = next.toUpperCase().slice(0, NAME_MAX);
          setInputValue(upper);
          // Typing away from the picked item un-picks it, so the row can never
          // save a name that does not match its item type.
          if (reason === "input" && value && upper !== value.name) onPick(null);
          if (reason === "clear") onPick(null);
        }}
        onChange={(_e, next) => {
          if (!next) return onPick(null);
          if (next.id === CREATE) return openCreate(next.name);
          pick(next);
        }}
        onBlur={pickExactOnBlur}
        renderInput={(params) => (
          <TextField
            {...params}
            inputRef={inputRef}
            size="small"
            label="Item Type *"
            placeholder={metalName ? `Name or code · ${metalName}` : "Name or code"}
            error={Boolean(error)}
            // Enter belongs to the list here (pick the highlighted item), not
            // to the screen's Enter-moves-to-next-field handler.
            inputProps={{ ...params.inputProps, "data-skip-enter-tab": "true" }}
            sx={error ? getFieldSx(true) : fieldSx}
          />
        )}
        renderOption={(props, option) => {
          const { key, ...rest } = props;
          if (option.id === CREATE) {
            return (
              <li
                key={CREATE}
                {...rest}
                style={{ borderTop: options.length ? `1px solid ${BORDER}` : "none" }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.25 }}>
                  <Box
                    sx={{
                      width: 26,
                      height: 26,
                      borderRadius: 1.5,
                      bgcolor: BLUE,
                      color: "#fff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    {option.restoring ? (
                      <RestoreIcon sx={{ fontSize: 17 }} />
                    ) : (
                      <AddIcon sx={{ fontSize: 18 }} />
                    )}
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: "0.82rem", fontWeight: 700, color: BLUE_DARK }}>
                      {option.restoring ? "Restore" : "Create"} “{option.name}”
                    </Typography>
                    <Typography sx={{ fontSize: "0.68rem", color: INK_MUTED }}>
                      {option.restoring
                        ? `Deleted earlier from ${metalName}`
                        : `New ${metalName} item type`}
                    </Typography>
                  </Box>
                </Box>
              </li>
            );
          }
          return (
            <li key={option.id} {...rest}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1,
                  width: "100%",
                }}
              >
                <Typography sx={{ fontSize: "0.84rem", fontWeight: 600, color: INK }}>
                  {option.name}
                </Typography>
                <CodeChip code={option.short_code} />
              </Box>
            </li>
          );
        }}
        slotProps={{
          paper: {
            sx: {
              mt: 0.5,
              borderRadius: 2,
              border: `1px solid ${BORDER}`,
              boxShadow: "0 10px 30px rgba(15,23,42,0.14)",
              minWidth: 240,
              "& .MuiAutocomplete-option": { minHeight: 38 },
              "& .MuiAutocomplete-option.Mui-focused": { bgcolor: `${BLUE_PALE} !important` },
            },
          },
        }}
      />

      {/* ── Create / restore an item type without leaving the loan ── */}
      <Dialog
        open={Boolean(dialog)}
        onClose={() => !saving && cancelDialog()}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}
      >
        <Box
          sx={{
            px: 2.5,
            py: 1.75,
            bgcolor: BLUE,
            color: "#fff",
            display: "flex",
            alignItems: "center",
            gap: 1.25,
          }}
        >
          <ItemIcon />
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 800, fontSize: "1rem", lineHeight: 1.2 }}>
              {dialog?.restoring ? "Restore item type" : "New item type"}
            </Typography>
            <Typography sx={{ fontSize: "0.72rem", opacity: 0.85 }}>
              {dialog?.forAdd
                ? `Save it to add this ${metalName} ornament`
                : `Added to Item Type Master for ${metalName}`}
            </Typography>
          </Box>
        </Box>
        <DialogContent
          sx={{ pt: "20px !important", display: "flex", flexDirection: "column", gap: 1.5 }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              saveDialog();
            }
          }}
        >
          <TextField
            autoFocus
            size="small"
            label="Item type name *"
            value={dialog?.name || ""}
            disabled={Boolean(dialog?.restoring)}
            onChange={(e) => {
              const name = e.target.value.toUpperCase().slice(0, NAME_MAX);
              setDialog((d) => ({ ...d, name, restoring: findDeleted(name) }));
              setDialogErrors((p) => ({ ...p, name: undefined }));
            }}
            error={Boolean(dialogErrors.name || dialogNameClash)}
            helperText={
              dialogErrors.name ||
              (dialogNameClash ? `${dialogNameClash.name} already exists` : " ")
            }
            sx={dialogErrors.name || dialogNameClash ? getFieldSx(true) : fieldSx}
            inputProps={{ autoComplete: "off" }}
          />
          <TextField
            size="small"
            label="Short code *"
            value={dialogCode}
            onChange={(e) => {
              setDialog((d) => ({
                ...d,
                code: normalizeShortCode(e.target.value),
                codeTouched: true,
              }));
              setDialogErrors((p) => ({ ...p, code: undefined }));
            }}
            error={Boolean(dialogErrors.code || dialogCodeClash)}
            helperText={
              dialogErrors.code ||
              (dialogCodeClash
                ? `Used by ${dialogCodeClash.name}${dialogCodeClash.is_active ? "" : " (deleted)"}`
                : dialog?.restoring
                ? "Kept from the deleted item — type to change"
                : !dialog?.codeTouched && dialogCode
                ? "Suggested — type to change. Typing it later finds this item."
                : `Letters & digits, up to ${CODE_MAX}`)
            }
            sx={{
              ...(dialogErrors.code || dialogCodeClash ? getFieldSx(true) : fieldSx),
              "& input": { fontFamily: "monospace", fontWeight: 700, letterSpacing: "0.06em" },
            }}
            inputProps={{ autoComplete: "off", maxLength: CODE_MAX }}
            InputProps={{
              endAdornment:
                !dialog?.codeTouched && dialogCode ? (
                  <InputAdornment position="end">
                    <AutoIcon sx={{ fontSize: 16, color: BLUE }} />
                  </InputAdornment>
                ) : null,
            }}
          />
          {dialog?.restoring && (
            <Box
              sx={{
                p: 1.25,
                borderRadius: 2,
                bgcolor: "#FFFBEB",
                border: "1px solid #FDE68A",
              }}
            >
              <Typography sx={{ fontSize: "0.75rem", color: WARN }}>
                {dialog.name} was deleted from {metalName}. Saving brings it back —
                with its old pledges still attached.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 2, gap: 1 }}>
          <Button
            onClick={cancelDialog}
            disabled={saving}
            sx={{ textTransform: "none", fontWeight: 700, color: INK_SOFT, borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={saveDialog}
            disabled={saving}
            startIcon={
              saving ? (
                <CircularProgress size={14} sx={{ color: "#fff" }} />
              ) : dialog?.restoring ? (
                <RestoreIcon />
              ) : (
                <AddIcon />
              )
            }
            sx={{
              textTransform: "none",
              fontWeight: 700,
              borderRadius: 2,
              boxShadow: "none",
              "&:hover": { boxShadow: "none" },
            }}
          >
            {dialog?.forAdd
              ? dialog?.restoring
                ? "Restore & add ornament"
                : "Create & add ornament"
              : dialog?.restoring
              ? "Restore & use"
              : "Create & use"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
});

export default ItemTypePicker;
